import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { toApi } from "@/lib/time";

// Unified calendar data (events + task blocks) for a date range, with optimistic mutations + rollback.
export default function useCalendarData(rangeStart, rangeEnd) {
  const [data, setData] = useState({ events: [], task_blocks: [], calendars: [], coverage: null });
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);

  const load = useCallback(async (silent = false) => {
    const my = ++seq.current;
    if (!silent) setLoading(true);
    try {
      const res = await api.get("/calendar/items", { params: { from: toApi(rangeStart), to: toApi(rangeEnd) } });
      if (my === seq.current) setData(res.data);
    } catch { toast.error("Impossible de charger l'agenda"); }
    finally { if (my === seq.current) setLoading(false); }
  }, [rangeStart, rangeEnd]);

  useEffect(() => { load(); }, [load]);

  // optimistic: apply local change, call API, rollback on failure with Retry.
  const optimistic = useCallback(async (apply, request, label) => {
    let snapshot;
    setData((prev) => { snapshot = prev; return apply(prev); });
    try {
      await request();
      load(true);
    } catch (e) {
      setData(snapshot);
      const msg = e?.response?.data?.detail || "Impossible d'enregistrer la modification.";
      toast.error(`${label ? label + " — " : ""}${msg}`, {
        action: { label: "Réessayer", onClick: () => optimistic(apply, request, label) },
      });
      throw e;
    }
  }, [load]);

  const moveBlock = (block, start, end) => optimistic(
    (p) => ({ ...p, task_blocks: p.task_blocks.map((b) => b.id === block.id ? { ...b, scheduled_start: toApi(start), scheduled_end: toApi(end) } : b) }),
    () => api.patch(`/task-blocks/${block.id}`, { start: toApi(start), end: toApi(end) }), "Déplacement");

  const moveEvent = (ev, start, end) => optimistic(
    (p) => ({ ...p, events: p.events.map((e) => e.key === ev.key ? { ...e, start_utc: toApi(start), end_utc: toApi(end) } : e) }),
    () => api.patch(`/calendar/events/${ev.id}`, { start: toApi(start), end: toApi(end), occurrence_key: ev.occurrence_key, scope: "this" }), "Déplacement");

  const unscheduleBlock = (block) => optimistic(
    (p) => ({ ...p, task_blocks: p.task_blocks.filter((b) => b.id !== block.id) }),
    () => api.delete(`/task-blocks/${block.id}`), "Déplanification");

  const deleteEvent = (ev) => optimistic(
    (p) => ({ ...p, events: p.events.filter((e) => e.key !== ev.key) }),
    () => api.delete(`/calendar/events/${ev.id}`, { params: ev.occurrence_key ? { occurrence_key: ev.occurrence_key } : {} }), "Suppression");

  const scheduleTask = (task, start, end) => optimistic(
    (p) => ({ ...p, task_blocks: [...p.task_blocks, { id: `tmp_${Date.now()}`, key: `tmp_${Date.now()}`, task_id: task.id, task,
      scheduled_start: toApi(start), scheduled_end: toApi(end), status: "planned", locked: false, item_type: "task_block", editable: true }] }),
    () => api.post(`/tasks/${task.id}/blocks`, { start: toApi(start), end: toApi(end) }), "Planification");

  const quickTask = (title, start, end) => optimistic(
    (p) => ({ ...p, task_blocks: [...p.task_blocks, { id: `tmp_${Date.now()}`, key: `tmp_${Date.now()}`, task_id: "tmp", task: { title, status: "planned" },
      scheduled_start: toApi(start), scheduled_end: toApi(end), status: "planned", locked: false, item_type: "task_block", editable: true }] }),
    () => api.post("/tasks/quick", { title, start: toApi(start), end: toApi(end) }), "Création");

  const toggleLock = (block) => optimistic(
    (p) => ({ ...p, task_blocks: p.task_blocks.map((b) => b.id === block.id ? { ...b, locked: !b.locked } : b) }),
    () => api.patch(`/task-blocks/${block.id}`, { locked: !block.locked }), "Verrouillage");

  const completeTask = (taskId) => optimistic(
    (p) => ({ ...p, task_blocks: p.task_blocks.map((b) => b.task_id === taskId ? { ...b, status: "done", task: { ...b.task, status: "done" } } : b) }),
    () => api.post(`/tasks/${taskId}/complete`), "Terminer");

  return { data, loading, reload: load, moveBlock, moveEvent, unscheduleBlock, deleteEvent, scheduleTask, quickTask, toggleLock, completeTask };
}
